import { InspectionDefect } from '@/types/InspectionDefect';
import { CheckpointCheck, InspectionCheckpointCheckResult } from '@/types/CheckpointCheck.type';
import {
  CHECKPOINT_CHECKS_COMPONENT_NAME,
  hasNestedCheckpointChecks,
  isCheckpointComplete,
  withParentCheckpointChecked,
} from '@/utils/checkpoint-checks.util';

export const CHECKLIST_COMPONENT_NAMES = new Set([
  'QuantityCheckList',
  'CartonCheckList',
  'ProductPackagingCheckList',
  'MeasurementCheckList',
  'ProductVisualCheckList',
  'SampleCheckDetails',
  'ProductSafetyCheckDetails',
  'CloudinarySamplePhotoDetails',
  CHECKPOINT_CHECKS_COMPONENT_NAME,
]);

export interface ChecklistTemplateEntry {
  type: string;
  name: string;
  header?: string | null;
  components?: ReadonlyArray<{ name: string }>;
}

export interface ChecklistTemplate {
  modules?: ReadonlyArray<ChecklistTemplateEntry>;
}

export interface DefectChecklistTransitionResult {
  qcDefects: InspectionDefect[];
  checkedModules: string;
  checkpointCheckResults?: InspectionCheckpointCheckResult[];
}

export function parseCheckedModules(checkedModules: string | undefined): string[] {
  if (!checkedModules?.trim()) {
    return [];
  }
  const seen = new Set<string>();
  const result: string[] = [];
  for (const part of checkedModules.split(',')) {
    const trimmed = part.trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      result.push(trimmed);
    }
  }
  return result;
}

export function serializeCheckedModules(names: ReadonlyArray<string>): string {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const name of names) {
    const trimmed = name.trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      result.push(trimmed);
    }
  }
  return result.join(',');
}

export function isChecklistModule(templateModule: ChecklistTemplateEntry): boolean {
  if (templateModule.type !== 'module') {
    return false;
  }
  const components = templateModule.components ?? [];
  return components.some((component) => CHECKLIST_COMPONENT_NAMES.has(component.name));
}

export function getChecklistModuleNames(template: ChecklistTemplate | null | undefined): string[] {
  if (!template?.modules) {
    return [];
  }
  return template.modules.filter(isChecklistModule).map((module) => module.name);
}

export function isModuleChecked(checkedModules: string | undefined, moduleName: string): boolean {
  return parseCheckedModules(checkedModules).includes(moduleName);
}

export function withModuleChecked(
  checkedModules: string | undefined,
  moduleName: string,
  isChecked: boolean
): string {
  const names = parseCheckedModules(checkedModules);
  const index = names.indexOf(moduleName);
  if (isChecked && index === -1) {
    names.push(moduleName);
  } else if (!isChecked && index > -1) {
    names.splice(index, 1);
  }
  return serializeCheckedModules(names);
}

export function getUncheckedChecklistModules(
  template: ChecklistTemplate | null | undefined,
  checkedModules: string | undefined,
  nested?: {
    checks: ReadonlyArray<CheckpointCheck>;
    results: ReadonlyArray<InspectionCheckpointCheckResult> | undefined;
    templateId: string | undefined;
  }
): Array<{ moduleName: string; header: string }> {
  const checkedSet = new Set(parseCheckedModules(checkedModules));
  return (template?.modules ?? [])
    .filter(isChecklistModule)
    .filter((module) => {
      if (nested && hasNestedCheckpointChecks(nested.checks, nested.templateId, module.name)) {
        return !isCheckpointComplete({
          checks: nested.checks,
          results: nested.results,
          templateId: nested.templateId,
          checkpointCode: module.name,
        });
      }
      return !checkedSet.has(module.name);
    })
    .map((module) => ({ moduleName: module.name, header: module.header ?? module.name }));
}

export function getDefectIdentity(defect: InspectionDefect): string | undefined {
  const persistedId = defect.id?.trim();
  if (persistedId) {
    return persistedId;
  }
  const localId = defect.newId?.trim();
  return localId || undefined;
}

export function getDefectIdentityCandidates(defect: InspectionDefect): string[] {
  const candidates: string[] = [];
  const persistedId = defect.id?.trim();
  const localId = defect.newId?.trim();
  if (persistedId) {
    candidates.push(persistedId);
  }
  if (localId && localId !== persistedId) {
    candidates.push(localId);
  }
  return candidates;
}

export function isSameDefect(a: InspectionDefect, b: InspectionDefect): boolean {
  const aIds = getDefectIdentityCandidates(a);
  const bIds = getDefectIdentityCandidates(b);
  return aIds.some((id) => bIds.includes(id));
}

export function resolveDefectModuleName(
  defect: InspectionDefect,
  qcDefects: ReadonlyArray<InspectionDefect> = []
): string | undefined {
  const directModule = defect.module?.trim();
  if (directModule) {
    return directModule;
  }
  const matchedDefect = qcDefects.find((candidate) => isSameDefect(candidate, defect));
  const matchedModule = matchedDefect?.module?.trim();
  return matchedModule || undefined;
}

export function findDefectByIdentity(
  qcDefects: ReadonlyArray<InspectionDefect>,
  defect: InspectionDefect
): InspectionDefect | undefined {
  return qcDefects.find((candidate) => isSameDefect(candidate, defect));
}

export function moduleHasRemainingDefects(
  qcDefects: ReadonlyArray<InspectionDefect>,
  moduleName: string,
  excludeDefect?: InspectionDefect
): boolean {
  return qcDefects.some((defect) => {
    if (excludeDefect && isSameDefect(defect, excludeDefect)) {
      return false;
    }
    return defect.module === moduleName;
  });
}

function toChecklistModuleSet(
  checklistModuleNames: ReadonlySet<string> | ReadonlyArray<string>
): ReadonlySet<string> {
  return checklistModuleNames instanceof Set ? checklistModuleNames : new Set(checklistModuleNames);
}

export function addDefectAndCheckModule(params: {
  qcDefects: ReadonlyArray<InspectionDefect>;
  checkedModules: string | undefined;
  defect: InspectionDefect;
  checklistModuleNames: ReadonlySet<string> | ReadonlyArray<string>;
  checkpointChecks?: ReadonlyArray<CheckpointCheck>;
  checkpointCheckResults?: ReadonlyArray<InspectionCheckpointCheckResult>;
  templateId?: string;
}): DefectChecklistTransitionResult {
  const { qcDefects, checkedModules, defect, checklistModuleNames } = params;
  const checklistSet = toChecklistModuleSet(checklistModuleNames);
  const newQcDefects = [...qcDefects, defect];
  const moduleName = defect.module?.trim();
  if (
    moduleName &&
    hasNestedCheckpointChecks(params.checkpointChecks ?? [], params.templateId, moduleName)
  ) {
    return {
      qcDefects: newQcDefects,
      checkedModules: checkedModules ?? '',
      checkpointCheckResults: withParentCheckpointChecked({
        checks: params.checkpointChecks ?? [],
        results: params.checkpointCheckResults,
        templateId: params.templateId,
        checkpointCode: moduleName,
        isChecked: true,
      }),
    };
  }
  const newCheckedModules =
    moduleName && checklistSet.has(moduleName)
      ? withModuleChecked(checkedModules, moduleName, true)
      : (checkedModules ?? '');
  return {
    qcDefects: newQcDefects,
    checkedModules: newCheckedModules,
    checkpointCheckResults: params.checkpointCheckResults
      ? [...params.checkpointCheckResults]
      : undefined,
  };
}

export function deleteDefectAndUpdateChecklist(params: {
  qcDefects: ReadonlyArray<InspectionDefect>;
  checkedModules: string | undefined;
  defectToDelete: InspectionDefect;
  checklistModuleNames: ReadonlySet<string> | ReadonlyArray<string>;
  checkpointChecks?: ReadonlyArray<CheckpointCheck>;
  checkpointCheckResults?: ReadonlyArray<InspectionCheckpointCheckResult>;
  templateId?: string;
}): DefectChecklistTransitionResult {
  const { qcDefects, checkedModules, defectToDelete, checklistModuleNames } = params;
  const checklistSet = toChecklistModuleSet(checklistModuleNames);
  const moduleName = resolveDefectModuleName(defectToDelete, qcDefects);
  const newQcDefects = qcDefects.filter((defect) => !isSameDefect(defect, defectToDelete));
  let newCheckedModules = checkedModules ?? '';
  let checkpointCheckResults = params.checkpointCheckResults
    ? [...params.checkpointCheckResults]
    : undefined;
  if (moduleName && checklistSet.has(moduleName)) {
    const hasRemaining = moduleHasRemainingDefects(newQcDefects, moduleName);
    if (!hasRemaining) {
      if (hasNestedCheckpointChecks(params.checkpointChecks ?? [], params.templateId, moduleName)) {
        checkpointCheckResults = withParentCheckpointChecked({
          checks: params.checkpointChecks ?? [],
          results: params.checkpointCheckResults,
          templateId: params.templateId,
          checkpointCode: moduleName,
          isChecked: false,
        });
      } else {
        newCheckedModules = withModuleChecked(checkedModules, moduleName, false);
      }
    }
  }
  return { qcDefects: newQcDefects, checkedModules: newCheckedModules, checkpointCheckResults };
}
